/*
 * ============================================================================
 * Pet Portrait — Likeness Proof compare card  (<pet-portrait-compare>)
 * ----------------------------------------------------------------------------
 * A single before/after drag-to-compare stage. The element IS the stage: it
 * holds the two stacked images (before underneath, after in a clipped overlay),
 * a draggable handle, and a visually-hidden <input type="range"> that is the
 * accessible control. Dragging (pointer) or the range/keyboard updates the CSS
 * custom property --pp-rl (reveal %), and the CSS clips the top image to it.
 *
 * Touch is deliberately narrower than mouse: on mobile the card is a slide of
 * a horizontal scroll-snap carousel, so only the handle drags — a swipe
 * elsewhere belongs to the carousel, a tap elsewhere jumps the reveal.
 *
 * One class-based custom element per card; the surrounding grid/carousel is
 * pure CSS (no JS). Self-contained + namespaced `pp-` for portability. Paints
 * are batched with requestAnimationFrame and listeners are cleaned up on
 * disconnect (no leaks).
 * ============================================================================
 */
(function () {
  'use strict';

  var TAG = 'pet-portrait-compare';

  // Travel (px) a touch may drift and still count as a tap rather than a swipe.
  var TAP_SLOP = 10;

  function clamp(v) {
    return Math.max(0, Math.min(100, v));
  }

  class PetPortraitCompare extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this.stageTop = this.querySelector('[data-pp-compare-top]');
      this.handle = this.querySelector('[data-pp-compare-handle]');
      this.range = this.querySelector('[data-pp-compare-range]');
      if (!this.stageTop || !this.handle) return;

      this._wired = true;
      this._pointerId = null;
      this._rect = null;
      this._pending = null;
      this._raf = 0;
      this._tap = null;

      // Bind once so add/removeEventListener see the same references.
      this._onDown = this.onPointerDown.bind(this);
      this._onMove = this.onPointerMove.bind(this);
      this._onUp = this.onPointerUp.bind(this);
      this._onEnd = this.cancelPointer.bind(this);
      this._onInput = this.onRangeInput.bind(this);
      this._onKey = this.onKey.bind(this);
      this._paint = this.paint.bind(this);

      this.addEventListener('pointerdown', this._onDown);
      this.addEventListener('pointermove', this._onMove);
      this.addEventListener('pointerup', this._onUp);
      this.addEventListener('pointercancel', this._onEnd);
      this.addEventListener('lostpointercapture', this._onEnd);
      if (this.range) this.range.addEventListener('input', this._onInput);
      this.addEventListener('keydown', this._onKey);

      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

      // Initial reveal: honour data-start (e.g. 50), else default to centre.
      var start = parseFloat(this.getAttribute('data-start'));
      if (isNaN(start)) start = 50;
      this.apply(start, true);
    }

    disconnectedCallback() {
      if (!this._wired) return;
      this.removeEventListener('pointerdown', this._onDown);
      this.removeEventListener('pointermove', this._onMove);
      this.removeEventListener('pointerup', this._onUp);
      this.removeEventListener('pointercancel', this._onEnd);
      this.removeEventListener('lostpointercapture', this._onEnd);
      if (this.range) this.range.removeEventListener('input', this._onInput);
      this.removeEventListener('keydown', this._onKey);
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
      this._tap = null;
      this._wired = false;
    }

    /* --- Painting (rAF-batched) ---------------------------------------- */
    paint() {
      this._raf = 0;
      if (this._pending === null) return;
      var v = this._pending;
      this.style.setProperty('--pp-rl', v);
      this.handle.style.left = v + '%';
      this._pending = null;
    }

    apply(v, immediate) {
      v = clamp(v);
      this._pending = v;
      if (this.range) this.range.value = v;
      if (immediate) {
        if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
        this.paint();
      } else if (!this._raf) {
        this._raf = requestAnimationFrame(this._paint);
      }
    }

    pctFromClientX(clientX) {
      var r = this._rect || this.getBoundingClientRect();
      if (!r.width) return 50;
      return ((clientX - r.left) / r.width) * 100;
    }

    /* --- Pointer drag --------------------------------------------------
     * On mobile this card is a slide of a horizontal scroll-snap carousel, so
     * a touch drag is ambiguous — "reveal more" and "next pair" are the same
     * gesture over the same pixels. Touch therefore only drags from the
     * handle (the one element with touch-action: none); a swipe anywhere else
     * is left to the carousel, while a stationary tap still jumps the reveal.
     * Mouse and pen keep drag-anywhere: nothing competes for their gestures.
     */
    onPointerDown(e) {
      if (e.button && e.button !== 0) return;

      if (e.pointerType === 'touch' && !this.isHandle(e.target)) {
        this._tap = { id: e.pointerId, x: e.clientX, y: e.clientY };
        return;
      }

      this._tap = null;
      this._pointerId = e.pointerId;
      this._rect = this.getBoundingClientRect();
      try { this.setPointerCapture(this._pointerId); } catch (err) {}
      this.classList.add('is-dragging');
      this.apply(this.pctFromClientX(e.clientX), true);
      e.preventDefault();
    }

    onPointerMove(e) {
      if (this._pointerId === null || e.pointerId !== this._pointerId) return;
      this.apply(this.pctFromClientX(e.clientX));
      e.preventDefault();
    }

    isHandle(node) {
      return !!(node && node.closest && node.closest('[data-pp-compare-handle]'));
    }

    /* A touch that never travelled is a tap: jump the reveal to it. If the
     * carousel took the gesture instead, pointercancel cleared _tap first. */
    onPointerUp(e) {
      var tap = this._tap;
      if (tap && e.pointerId === tap.id) {
        this._tap = null;
        if (Math.abs(e.clientX - tap.x) <= TAP_SLOP && Math.abs(e.clientY - tap.y) <= TAP_SLOP) {
          this.apply(this.pctFromClientX(e.clientX), true);
        }
      }
      this.endDrag(e);
    }

    cancelPointer(e) {
      if (this._tap && (!e || e.pointerId == null || e.pointerId === this._tap.id)) this._tap = null;
      this.endDrag(e);
    }

    endDrag(e) {
      if (this._pointerId === null) return;
      if (e && e.pointerId != null && e.pointerId !== this._pointerId) return;
      try { this.releasePointerCapture(this._pointerId); } catch (err) {}
      this._pointerId = null;
      this._rect = null;
      this.classList.remove('is-dragging');
    }

    /* --- Accessible range + keyboard ----------------------------------- */
    onRangeInput() {
      this.apply(parseFloat(this.range.value), true);
    }

    onKey(e) {
      var cur = this.range ? parseFloat(this.range.value) || 50 : 50;
      if (e.key === 'ArrowLeft') { this.apply(cur - 4, true); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { this.apply(cur + 4, true); e.preventDefault(); }
      else if (e.key === 'Home') { this.apply(0, true); e.preventDefault(); }
      else if (e.key === 'End') { this.apply(100, true); e.preventDefault(); }
    }
  }

  if (window.customElements && !window.customElements.get(TAG)) {
    window.customElements.define(TAG, PetPortraitCompare);
  }
})();
